import { useState } from "react";

import { format as formatDateFn } from "date-fns";
import _ from "lodash";

import { useOptionsHandlers } from "hooks";
import { TInputAttributeLang } from "model/application/Lang";
import { TViewMode } from "model/application/modal/CreateEditModal";

import { IInputOption } from "../BaseOptionsContainer";
import { IInputBaseLayout } from "../InputBaseLayout/InputBaseLayout";
import InputMultipleOptions from "../InputMultipleOptions";
import CreateDateDialog from "./CreateDateDialog";
import { mapDatesToSelectOptions, TDate } from "./utils";

interface IInputMultipleDatePropsBase extends IInputBaseLayout {
  defaultValues?: TDate[];
  name: string;
  onChange: (values: TDate[], name: string) => void;
  returnFormatted?: boolean;
  format?: string;
  langlabel: Pick<TInputAttributeLang, "title" | "tooltip" | "placeholder">;
  required?: boolean;
  viewMode?: TViewMode;
  viewStacked?: boolean;
  error?: string;
}

type TOption = IInputOption<TDate>;
const InputMultipleDate = ({
  name,
  viewMode,
  langlabel,
  defaultValues = [],
  format = "yyyy-MM-dd",
  returnFormatted = false,
  onChange,
}: IInputMultipleDatePropsBase) => {
  const [options, setOptions] = useState<TOption[]>(
    mapDatesToSelectOptions(defaultValues, format, returnFormatted),
  );

  const onChangeOptions = (options: TOption[]) => {
    const uniqueOptions = _.uniqBy(options, "value");
    const newDates = _.map(uniqueOptions, (option) => option.value);
    setOptions(uniqueOptions);
    const result = returnFormatted
      ? newDates.map((d) => formatDateFn(d, format))
      : newDates;
    onChange(_.compact(result), name);
  };

  const currentDate = new Date();
  const defaultOption = mapDatesToSelectOptions(
    [currentDate],
    format,
    returnFormatted,
  )[0];

  const {
    optionToCreate,
    optionToEdit,
    onDeleteOption,
    setOptionToCreate,
    setOptionToEdit,
    onConfirmAddOption,
    onConfirmEditOption,
    onOpenAddOption,
  } = useOptionsHandlers({
    options,
    onChangeOptions,
    defaultValue: defaultOption.value,
  });

  return (
    <>
      <InputMultipleOptions
        viewMode={viewMode}
        label={langlabel?.title}
        tooltip={langlabel?.tooltip}
        placeholder={langlabel?.placeholder}
        options={options}
        onChangeOptions={onChangeOptions}
        onOpenAddOption={onOpenAddOption}
        onClickOption={setOptionToEdit}
        onDeleteOption={onDeleteOption}
        draggable
      />

      {optionToCreate && (
        <CreateDateDialog
          open
          defaultOption={defaultOption}
          onSave={onConfirmAddOption}
          onClose={() => {
            setOptionToCreate(null);
          }}
          format={format}
          returnFormatted={returnFormatted}
        />
      )}

      {optionToEdit && (
        <CreateDateDialog
          open
          defaultOption={optionToEdit}
          onSave={onConfirmEditOption}
          onClose={() => {
            setOptionToEdit(null);
          }}
          format={format}
          returnFormatted={returnFormatted}
        />
      )}
    </>
  );
};

export default InputMultipleDate;
